import { ChangeDetectorRef, Component, Injector, NgZone, QueryList, ViewChildren } from '@angular/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ActionSheetController, AlertController, IonRouterOutlet, ModalController, Platform, PopoverController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { AlertService } from './services/alert.service';
import { LoggerService } from './services/logger.service';
import { ConnectionStatus, NetworkService } from './services/network.service';
import { StorageService } from './services/storage.service';
import { UpdateService } from './services/update.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { Location } from '@angular/common';
import { App } from '@capacitor/app';
import { Router } from '@angular/router';
import { LanguageService } from './services/language.service';
import { MenuController } from '@ionic/angular';
import { MasterdataApiService } from './services/masterdata-api.service';
import { OktaService } from './services/okta.service';
import { AppEventService } from './services/app-event-service';
import { UserSettingsComponent } from './pages/user-settings/user-settings.component';
import { LoginPage } from './pages/login/login.page';
import { PreConfigurationModalComponent } from './components/pre-configuration-modal/pre-configuration-modal.component';
import { AccountComponent } from './pages/account/account.component';
import { Browser } from '@capacitor/browser';
import { ThermalApiService } from './services/thermal-api.service';
import { AgreementModalComponent } from './components/agreement-modal/agreement-modal.component';
import { AppInfoModalComponent } from './components/app-info-modal/app-info-modal.component';

type CookieConsent = {
  necessary: boolean;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
  consentedAt: string;
  version: string;
};

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
  providers: []
})
export class AppComponent {
  private readonly cookieConsentStorageKey = 'iCaluwin-CookieConsent';
  private readonly cookieConsentVersion = '2026-07';
  private lastTimeBackPress = 0;
  private timePeriodToExit = 2000; // 2 seconds gap between back presses
  activePage: any;
  language: string = "en";
  languages: any = [];
  networkStateSubscribe: any;
  @ViewChildren(IonRouterOutlet)
  routerOutlets!: QueryList<IonRouterOutlet>;

  isLoggedIn: boolean = false;
  showCookieConsentBanner = false;
  // Cookie consent is a browser/GDPR concept and must never show in the native app shell
  isNativeApp = false;
  cookieConsent: CookieConsent | null = null;
  private oktaServiceRef?: OktaService;
  callbackHandled: boolean = false;;
  constructor(
    private platform: Platform,
    private router: Router,
    private injector: Injector,
    public translateService: TranslateService,
    public languageService: LanguageService,
    private storage: StorageService,
    public logger: LoggerService,
    private networkService: NetworkService,
    private _location: Location,
    public modalCtrl: ModalController,
    public alertController: AlertController,
    private popoverCtrl: PopoverController,
    private actionSheetCtrl: ActionSheetController,
    private toastCtrl: ToastController,
    public alertService: AlertService,
    public updateService: UpdateService,
    private menuCtrl: MenuController,
    private masterdataApi: MasterdataApiService,
    private cdr: ChangeDetectorRef,
    private appEvents: AppEventService,
    private modalController: ModalController,
    private storageService: StorageService,
    private thermalApi: ThermalApiService,
    private ngZone: NgZone
  ) {
    this.initializeApp();

    // network change 
    this.networkService.getNetWorkStatus();
    this.networkStateSubscribe = this.networkService.onNetworkChange().subscribe((status: any) => {
      if (status == ConnectionStatus.Online) {
        this.logger.info('app online');
      } else {
        this.logger.info('app offline');
      }
    });
    //this.listenForOktaCallback();

  }

  ngOnInit() {
    this.isNativeApp = this.platform.is('hybrid');
    this.initializeCookieConsent();

    this.appEvents.menuClicked$.subscribe(async () => {
      if (!this.hasCookieConsent()) {
        this.isLoggedIn = false;
        this.cdr.detectChanges();
        return;
      }

      this.isLoggedIn = await this.getOktaService().isUserAuthenticated();
      this.cdr.detectChanges();
    });

    this.masterdataApi.loadMasterData().subscribe({
      next: async data => {
        console.log('Master data loaded', data);
        await this.storageService.saveCurrentProject();
        this.loadInitialUw();
      },
      error: err => {
        console.error('Failed to load master data', err);
      }
    });
    //this.listenForOktaCallback();
  }

  initializeApp() {
    console.log("calling initializeApp")
    this.platform.ready().then(async () => {
      try {
        await SplashScreen.hide();
      } catch (e) {
        console.warn('Splash hide failed:', e);
      }
      this.setupBackButton();
      try {
        await this.languageService.initLanguage();
      } catch (e) {
        console.warn('Language init failed:', e);
      }
      try {
        await this.showAgreementIfNeeded();
      } catch (e) {
        console.warn('Agreement modal failed:', e);
      }
      // const appLanguage = this.languageService.getCurrentLanguage();
      // this.translateService.setDefaultLang('en');
      // this.translateService.use(appLanguage);
      // Subscribe to language changes
      // this.languageService.currentLanguage$.subscribe(lang => {
      //   this.translateService.use(lang);
      //   this.translateService.setDefaultLang(lang);
      // });

      setTimeout(() => {
        SplashScreen.hide({
          fadeOutDuration: 1000
        });
      }, 2000)

      this.platform.resume.subscribe(async () => {
        setTimeout(() => {
          console.log("Resume")
          // this.appUpdateCheck();
        }, 1200);
      });

      if (this.platform.is('cordova')) {
        await ScreenOrientation.lock({ orientation: 'portrait' });
        // await StatusBar.setStyle({ style: Style.Default });
        if (this.platform.is('android')) {
          StatusBar.setOverlaysWebView({ overlay: false });
          await StatusBar.setStyle({ style: Style.Light });
          StatusBar.setBackgroundColor({
            color: "#FFFFFFFF"
          })
        }
      }
      setTimeout(() => {
        // this.appUpdateCheck();
      }, 1200);
    });
  }

  loadInitialUw() {
    const raw = localStorage.getItem('iCaluwin-LastProject');
    const mainProject = raw ? JSON.parse(raw) : {};
    if (!mainProject?.id && !mainProject?.saveOnlyGlass) {
      this.thermalApi.calculateBoth().subscribe({
        next: (res) => {
          const raw = localStorage.getItem('iCaluwin-LastProject');
          const mainProject = raw ? JSON.parse(raw) : {};
          mainProject.uw = res.uw.value;
          mainProject.uwUnit = res.uw.unit;
          mainProject.psi = res.psi.value;
          mainProject.psiUnit = res.psi.unit;
          localStorage.setItem('iCaluwin-LastProject', JSON.stringify(mainProject));
        },
        error: (err) => {
          console.error('Thermal calculation failed:', err);
        }
      });
    }
  }

  async setupBackButton() {
    App.addListener('backButton', async ({ canGoBack }) => {
      const currentRoute = this.router.url;

      // close action sheet
      try {
        const element = await this.actionSheetCtrl.getTop();
        if (element) {
          await element.dismiss();
          return;
        }
      } catch (error) {
        console.log(error)
      }
      // close popover
      try {
        const element = await this.popoverCtrl.getTop();
        if (element) {
          await element.dismiss();
          return;
        }
      } catch (error) {
        console.log(error)
      }
      // close modal
      try {
        const element = await this.modalCtrl.getTop();
        if (element) {
          const cssClass = element.cssClass;
          const classes = Array.isArray(cssClass) ? cssClass : [cssClass];
          if (classes.includes('agreement-modal')) {
            return; // do not close mandatory agreement modal
          }
          await element.dismiss();
          return;
        }
      } catch (error) {
        console.log(error)
      }
      //alert
      try {
        const element = await this.alertController.getTop();
        if (element) {
          await element.dismiss();
          return;
        }
      } catch (error) {
        console.log(error)
      }

      // If not on home page, use normal back navigation
      if (currentRoute !== '/home' && !currentRoute.includes("/tabs") && canGoBack) {
        window.history.back();
        return;
      }
      // Double tap to exit logic
      const currentTime = new Date().getTime();

      if (currentTime - this.lastTimeBackPress < this.timePeriodToExit) {
        App.exitApp(); // Close the app
      } else {
        this.lastTimeBackPress = currentTime;
        this.showToast(this.translateService.instant("exitApp"));
      }
    });
  }

  private async showToast(message: string) {
    const toast = await this.toastCtrl.create({
      message,
      duration: this.timePeriodToExit,
      position: 'bottom'
    });
    toast.present();
  }

  async onClose() {
    await this.menuCtrl.close('main-menu'); // Use your menuId
  }

  async userSettings() {
    const modal = await this.modalController.create({
      component: UserSettingsComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        // Pass any data if needed
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();
    console.log("login pages called")
    //this.router.navigate(['/login']);
    if (role === 'confirm' && data) {
      console.log('Selected option:', data.selectedOption);
      this.router.navigate(['/tabs/landing'], {
        queryParams: {
          configType: data.selectedOption
        }
      });
    }
  }

  async openHelpPage() {
    try {
      await Browser.open({
        url: 'https://www.swisspacer.com/en/contact-us'
      });
    } catch (error) {
      console.error('Failed to open help page:', error);
    }
  }

  async openInfoPage() {
    const appVersion = await this.getAppVersion();
    const modal = await this.modalController.create({
      component: AppInfoModalComponent,
      cssClass: 'app-info-fullscreen',
      componentProps: {
        appVersion
      }
    });
    await modal.present();
  }

  async userAccount() {
    const modal = await this.modalController.create({
      component: AccountComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        // Pass any data if needed
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();
    console.log("login pages called")
    //this.router.navigate(['/login']);
    if (role === 'confirm' && data) {
      console.log('Selected option:', data.selectedOption);
      this.router.navigate(['/tabs/landing'], {
        queryParams: {
          configType: data.selectedOption
        }
      });
    }
  }

  async openConfigurationModal() {
    this.onClose();
    this.storageService.saveCurrentProject();
    const modal = await this.modalController.create({
      component: PreConfigurationModalComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        // Pass any data if needed
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm' && data) {
      console.log('Selected option:', data.selectedOption);
      // this.router.navigate(['/tabs/configuration'], {
      //   queryParams: {
      //     configType: data.selectedOption
      //   }
      // });
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/tabs/configuration'], {
          queryParams: { configType: data.selectedOption }
        });
      });
    }
  }

  async openLogin() {
    const modal = await this.modalController.create({
      component: LoginPage,
      cssClass: 'configuration-modal',
      componentProps: {
        // Pass any data if needed
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();
    console.log("login pages called")
    //this.router.navigate(['/login']);
    if (role === 'confirm' && data) {
      console.log('Selected option:', data.selectedOption);
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/tabs/configuration'], {
          queryParams: { configType: data.selectedOption }
        });
      });
    }
  }

  openSavedConfigurationModal() {
    this.onClose();
    this.router.navigate(['/tabs/landing']);
  }

  openCookieSettings() {
    if (this.isNativeApp) {
      return;
    }
    this.showCookieConsentBanner = true;
    this.onClose();
  }

  acceptEssentialCookies() {
    this.cookieConsent = {
      necessary: true,
      preferences: false,
      analytics: false,
      marketing: false,
      consentedAt: new Date().toISOString(),
      version: this.cookieConsentVersion
    };
    localStorage.setItem(this.cookieConsentStorageKey, JSON.stringify(this.cookieConsent));
    this.showCookieConsentBanner = false;
    this.isLoggedIn = false;
  }

  acceptAllCookies() {
    this.cookieConsent = {
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
      consentedAt: new Date().toISOString(),
      version: this.cookieConsentVersion
    };
    localStorage.setItem(this.cookieConsentStorageKey, JSON.stringify(this.cookieConsent));
    this.showCookieConsentBanner = false;
    this.refreshAuthState();
  }

  private initializeCookieConsent() {
    // Native apps (Android/iOS) don't use cookies for tracking – skip the banner entirely
    if (this.isNativeApp) {
      this.showCookieConsentBanner = false;
      this.cookieConsent = {
        necessary: true,
        preferences: true,
        analytics: true,
        marketing: true,
        consentedAt: new Date().toISOString(),
        version: this.cookieConsentVersion
      };
      this.refreshAuthState();
      return;
    }

    const rawConsent = localStorage.getItem(this.cookieConsentStorageKey);
    if (!rawConsent) {
      this.showCookieConsentBanner = true;
      return;
    }

    try {
      this.cookieConsent = JSON.parse(rawConsent) as CookieConsent;
      this.showCookieConsentBanner = !this.cookieConsent?.version;
      if (!this.showCookieConsentBanner) {
        this.refreshAuthState();
      }
    } catch {
      this.showCookieConsentBanner = true;
    }
  }

  private hasCookieConsent(): boolean {
    return !!this.cookieConsent?.version;
  }

  private getOktaService(): OktaService {
    if (!this.oktaServiceRef) {
      this.oktaServiceRef = this.injector.get(OktaService);
    }
    return this.oktaServiceRef;
  }

  private async refreshAuthState() {
    if (!this.hasCookieConsent()) {
      this.isLoggedIn = false;
      return;
    }

    this.isLoggedIn = await this.getOktaService().isUserAuthenticated();
    this.cdr.detectChanges();
  }

  userLogout() {
    this.getOktaService().logout();
    this.onClose();
  }

  private async getAppVersion(): Promise<string> {
    try {
      const info = await App.getInfo();
      return info.version || '1.0.0';
    } catch (error) {
      console.warn('Could not get app version:', error);
      return '1.0.0';
    }
  }

  private async showAgreementIfNeeded(): Promise<void> {
    // const accepted = localStorage.getItem(this.AGREEMENT_KEY);

    // if (accepted === 'true') return;

    const appVersion = await this.getAppVersion();

    const modal = await this.modalController.create({
      component: AgreementModalComponent,
      cssClass: 'agreement-modal',
      backdropDismiss: false,
      canDismiss: async (_data?: any, role?: string) => {
        return role === 'confirm';
      },
      componentProps: {
        appVersion
      }
    });

    await modal.present();

    const { role, data } = await modal.onWillDismiss();

    // if (role === 'confirm' && data?.accepted) {
    //   localStorage.setItem(this.AGREEMENT_KEY, 'true');
    // }
  }
}